import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeftOutlined, BulbOutlined, RocketOutlined, UserOutlined, WarningOutlined } from "@ant-design/icons";
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  Layout,
  message,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from "antd";
import { useAuthenticatedFetch } from "@/Api/Axios";
import AppLayout from "@/Components/Common/AppLayout";
import PhoneInputWithCountryCode, { getPhoneDigits } from "@/Components/Common/PhoneInputWithCountryCode";
import { LoadingContext } from "@/Context/LoadingContext";
import { UserContext } from "@/Context/UserContext";

const { Title, Text } = Typography;
const { Content } = Layout;
const { TextArea } = Input;
const { Option } = Select;

const ResumeSetup = () => {
  const navigate = useNavigate();
  const fetch = useAuthenticatedFetch();
  const { startLoading, stopLoading } = useContext(LoadingContext);
  const { userProfileData, updateProfileInfo } = useContext(UserContext);
  const [form] = Form.useForm();
  const [error, setError] = useState("");
  const [planInfo, setPlanInfo] = useState({
    plan: "free",
    maxResumes: 3,
    currentResumeCount: 0,
    canCreateResume: true,
  });
  const [checkingPlan, setCheckingPlan] = useState(true);

  // Auth guard - redirect to login if not authenticated
  useEffect(() => {
    const authToken = localStorage.getItem("authorization");
    const userId = localStorage.getItem("userId");
    if (!authToken || !userId) {
      navigate("/app/login?returnUrl=" + encodeURIComponent(window.location.pathname));
    }
  }, [navigate]);

  // Fetch plan info to check if user can create resume
  useEffect(() => {
    const checkPlanInfo = async () => {
      try {
        setCheckingPlan(true);
        const userId = localStorage.getItem("userId");
        if (!userId) return;

        const response = await fetch.get(`/user/dashboard/${userId}`, false);
        if (response.success && response.data.planInfo) {
          setPlanInfo(response.data.planInfo);
          // If user can't create resume, redirect to dashboard
          if (!response.data.planInfo.canCreateResume) {
            message.warning("You have reached your resume limit.");
            navigate("/app/dashboard");
          }
        }
      } catch (error) {
        console.error("Error checking plan info:", error);
      } finally {
        setCheckingPlan(false);
      }
    };
    checkPlanInfo();
  }, []);

  // Job role suggestions based on common roles
  const jobRoleSuggestions = [
    "Software Engineer",
    "Frontend Developer",
    "Backend Developer",
    "Full Stack Developer",
    "Data Scientist",
    "Product Manager",
    "UI/UX Designer",
    "DevOps Engineer",
    "Marketing Manager",
    "Sales Representative",
    "Business Analyst",
    "Project Manager",
    "Accountant",
    "Human Resources Specialist",
    "Customer Success Manager",
    "Content Writer",
    "Graphic Designer",
    "Digital Marketing Specialist",
    "Financial Analyst",
    "Operations Manager",
  ];

  // Industry options
  const industries = [
    "Technology",
    "Healthcare",
    "Finance",
    "Education",
    "Retail",
    "Manufacturing",
    "Real Estate",
    "Marketing & Advertising",
    "Consulting",
    "Non-profit",
    "Government",
    "Entertainment",
    "Transportation",
    "Energy",
    "Other",
  ];

  const handleBack = () => {
    navigate("/app/resume-choice");
  };

  const handleSubmit = async (values) => {
    // Double-check plan limit before creating
    if (!planInfo.canCreateResume) {
      setError("You have reached your resume limit. Free plan allows only 3 resumes.");
      return;
    }

    setError("");
    startLoading("Setting up your resume...");

    try {
      // Create blank resume with initial context and personal information
      const userId = localStorage.getItem("userId");
      const response = await fetch.post(
        "/create-blank-resume",
        {
          // Personal Information
          fullName: values.fullName,
          email: values.email,
          mobile: values.mobile?.fullNumber || "",
          // Career Information
          targetRole: values.targetRole,
          experienceLevel: values.experienceLevel,
          industry: values.industry || null,
          userBackground: values.userBackground || null,
          userId: userId, // Include userId if logged in
        },
        false
      ); // Pass false to disable automatic loading

      if (!response.success) {
        throw new Error(response.message || "Failed to create resume");
      }

      const resumeData = response.data;

      // Update user profile with new resumeId (backend already links it)
      if (resumeData?.resumeId) {
        updateProfileInfo({ ...userProfileData, resumeId: resumeData.resumeId });

        // Track analytics for successful setup
        if (typeof gtag !== "undefined") {
          gtag("event", "resume_setup_completed", {
            target_role: values.targetRole,
            experience_level: values.experienceLevel,
            industry: values.industry || "not_specified",
          });
        }

        stopLoading();

        // Navigate directly to resume view
        navigate(`/app/resume/${resumeData.resumeId}`, {
          state: {
            isNewResume: true,
          },
        });
      } else {
        throw new Error("No resume ID received");
      }
    } catch (err) {
      stopLoading();
      console.error("Resume setup error", err.message || err);
      setError(err.message || "Failed to set up resume. Please try again.");
    }
  };


  // Show loading while checking plan
  if (checkingPlan) {
    return (
      <AppLayout>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <Spin size="large" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <style>{`
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          box-sizing: border-box !important;
          height: 100% !important;
          width: 100% !important;
          overflow-x: clip !important;
          overflow-y: auto !important;
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
        }
        *, *::before, *::after { box-sizing: border-box !important; }
        .ant-layout, .ant-layout-content { margin: 0 !important; padding: 0 !important; position: relative !important; }
        .ant-layout { overflow: visible !important; }
        body { user-select: none !important; -webkit-user-select: none !important; -moz-user-select: none !important; -ms-user-select: none !important; }

        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
          .back-button-container {
            left: 60px !important;
            top: 12px !important;
          }

          .back-to-options-btn {
            height: 32px !important;
            paddingLeft: 8px !important;
            paddingRight: 8px !important;
            fontSize: 12px !important;
          }

          .back-button-text {
            display: none;
          }
        }
      `}</style>
      <Content
        style={{
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "100vh",
          overflowY: "auto",
        }}
      >
        {/* Header with back button */}
        <div
          className="back-button-container"
          style={{
            position: "absolute",
            top: "20px",
            left: "80px",
            zIndex: 10,
          }}
        >
          <Button
            type="default"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            className="back-to-options-btn"
            style={{
              fontSize: "14px",
              borderColor: "#1890ff",
              color: "#1890ff",
              backgroundColor: "transparent",
              height: "36px",
              paddingLeft: "16px",
              paddingRight: "16px",
              borderRadius: "6px",
              fontWeight: "500",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              transition: "all 0.3s ease",
            }}
          >
            <span className="back-button-text">Back to options</span>
          </Button>
        </div>

        {/* Title Section */}
        <div
          style={{
            marginTop: "60px",
            marginBottom: "16px",
            textAlign: "center",
            maxWidth: "600px",
            padding: "0 16px",
          }}
        >
          <Title level={2} style={{ margin: "0 0 8px 0", fontSize: "clamp(1.25rem, 5vw, 1.6rem)", fontWeight: "700" }}>
            🚀 Let's create your resume from scratch
          </Title>
          <Text
            style={{
              fontSize: "clamp(0.75rem, 3vw, 0.8125rem)",
              color: "#666",
              lineHeight: "1.4",
            }}
          >
            Tell us a few details and our AI will guide you through building a professional resume
          </Text>
        </div>

        {/* Setup Form */}
        <Card
          style={{
            width: "100%",
            maxWidth: "600px",
            borderRadius: "12px",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
            margin: "0 16px",
          }}
          bodyStyle={{ padding: "clamp(12px, 4vw, 20px)" }}
        >
          {error && (
            <Alert
              message="Setup Error"
              description={error}
              type="error"
              closable
              onClose={() => setError("")}
              style={{ marginBottom: "24px" }}
            />
          )}

          <Form form={form} layout="vertical" onFinish={handleSubmit} size="middle">
            <Row gutter={24}>
              {/* Personal Information - Left Side */}
              <Col xs={24} md={12}>
                <div style={{ marginBottom: "16px" }}>
                  <Title
                    level={5}
                    style={{
                      margin: "0 0 12px 0",
                      color: "#1890ff",
                      fontSize: "15px",
                      fontWeight: "600",
                    }}
                  >
                    Personal Information
                  </Title>

                  {/* Full Name */}
                  <Form.Item
                    label={<span style={{ fontSize: "13px", fontWeight: "600" }}>Full Name *</span>}
                    name="fullName"
                    rules={[
                      { required: true, message: "Please enter your full name" },
                      { min: 2, message: "Name must be at least 2 characters long" },
                      { max: 50, message: "Name must be less than 50 characters" },
                    ]}
                    style={{ marginBottom: "16px" }}
                  >
                    <Input placeholder="Enter your full name" style={{ fontSize: "13px" }} />
                  </Form.Item>

                  {/* Email */}
                  <Form.Item
                    label={<span style={{ fontSize: "13px", fontWeight: "600" }}>Email Address *</span>}
                    name="email"
                    rules={[
                      { required: true, message: "Please enter your email address" },
                      { type: "email", message: "Please enter a valid email address" },
                    ]}
                    style={{ marginBottom: "16px" }}
                  >
                    <Input placeholder="your.email@example.com" style={{ fontSize: "13px" }} />
                  </Form.Item>

                  {/* Mobile Number */}
                  <Form.Item
                    label={<span style={{ fontSize: "13px", fontWeight: "600" }}>Mobile Number *</span>}
                    name="mobile"
                    validateTrigger="onBlur"
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value?.phoneNumber) {
                            return Promise.reject(new Error("Please enter your mobile number"));
                          }
                          const requiredDigits = getPhoneDigits(value.countryCode);
                          if (requiredDigits) {
                            if (value.phoneNumber.length !== requiredDigits) {
                              return Promise.reject(new Error(`Phone number must be ${requiredDigits} digits`));
                            }
                          } else if (!/^\d{6,15}$/.test(value.phoneNumber)) {
                            return Promise.reject(new Error("Please enter a valid mobile number"));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                    style={{ marginBottom: "16px" }}
                  >
                    <PhoneInputWithCountryCode
                      placeholder="Enter phone number"
                      inputStyle={{ fontSize: "13px" }}
                    />
                  </Form.Item>
                </div>
              </Col>

              {/* Career Information - Right Side */}
              <Col xs={24} md={12}>
                <div style={{ marginBottom: "16px" }}>
                  <Title
                    level={5}
                    style={{
                      margin: "0 0 12px 0",
                      color: "#1890ff",
                      fontSize: "15px",
                      fontWeight: "600",
                    }}
                  >
                    Career Information
                  </Title>

                  {/* Target Role */}
                  <Form.Item
                    label={
                      <span style={{ fontSize: "13px", fontWeight: "600" }}>
                        What role are you applying for? *
                      </span>
                    }
                    name="targetRole"
                    rules={[
                      { required: true, message: "Please enter the target role" },
                      { min: 2, message: "Role must be at least 2 characters long" },
                    ]}
                    style={{ marginBottom: "16px" }}
                  >
                    <AutoComplete
                      placeholder="e.g., Software Engineer, Marketing Manager"
                      options={jobRoleSuggestions.map((role) => ({ value: role }))}
                      filterOption={(inputValue, option) =>
                        option.value.toLowerCase().includes(inputValue.toLowerCase())
                      }
                      style={{ fontSize: "13px" }}
                    />
                  </Form.Item>

                  {/* Experience Level */}
                  <Form.Item
                    label={<span style={{ fontSize: "13px", fontWeight: "600" }}>Your experience level *</span>}
                    name="experienceLevel"
                    rules={[{ required: true, message: "Please select your experience level" }]}
                    style={{ marginBottom: "16px" }}
                  >
                    <Select placeholder="Select your experience level" style={{ fontSize: "13px" }}>
                      <Option value="entry">Entry Level (0-2 years)</Option>
                      <Option value="mid">Mid Level (3-5 years)</Option>
                      <Option value="senior">Senior Level (5+ years)</Option>
                      <Option value="executive">Executive Level (10+ years)</Option>
                    </Select>
                  </Form.Item>

                  {/* Industry */}
                  <Form.Item
                    label={<span style={{ fontSize: "13px", fontWeight: "600" }}>Industry (optional)</span>}
                    name="industry"
                    style={{ marginBottom: "16px" }}
                  >
                    <Select placeholder="Select your industry" allowClear style={{ fontSize: "13px" }}>
                      {industries.map((industry) => (
                        <Option key={industry} value={industry}>
                          {industry}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              </Col>
            </Row>

            {/* User Background - Full width */}
            <Form.Item
              label={
                <span style={{ fontSize: "13px", fontWeight: "600" }}>
                  Tell us a bit about yourself (optional)
                </span>
              }
              name="userBackground"
              style={{ marginBottom: "16px" }}
            >
              <TextArea
                placeholder="e.g., Recent computer science graduate with internship experience in web development..."
                rows={4}
                maxLength={500}
                showCount
                style={{ fontSize: "13px" }}
              />
            </Form.Item>

            <div
              style={{
                padding: "12px",
                backgroundColor: "#e6f7ff",
                borderRadius: "6px",
                border: "1px solid #91d5ff",
                marginTop: "24px",
                marginBottom: "20px",
              }}
            >
              <Text style={{ fontSize: "13px", color: "#1890ff", fontWeight: "500" }}>
                💡 Pro Tip: The more details you provide, the better our AI can personalize your resume content and
                suggestions.
              </Text>
            </div>

            {/* Submit Button */}
            <Form.Item style={{ marginBottom: 0, textAlign: "center" }}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                icon={<RocketOutlined />}
                className="start-building-btn"
                style={{
                  backgroundColor: "#1890ff",
                  borderColor: "#1890ff",
                  borderRadius: "6px",
                  fontWeight: "600",
                  height: "44px",
                  paddingLeft: "24px",
                  paddingRight: "24px",
                  fontSize: "15px",
                  boxShadow: "0 2px 8px rgba(24, 144, 255, 0.3)",
                  transition: "all 0.3s ease",
                }}
              >
                Start Building ✨
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Content>
    </AppLayout>
  );
};

export default ResumeSetup;
